'use client';

import { useState, useEffect } from 'react';
import { WhatsappLogo, FacebookLogo, XLogo, PinterestLogo, LinkSimple, Check, ShareNetwork } from '@phosphor-icons/react';
type ShareButtonsProps = {
  url: string;
  title: string;
  description?: string;
  imageUrl?: string;
  locale: string;
};

/**
 * Attribution params, kept deliberately short.
 *
 * These used to add utm_source + utm_medium + utm_campaign — about 75
 * characters on every share. Instagram story links have to be read and typed
 * by people, and that much query string made them unusable.
 *
 * utm_source and utm_medium stay: analytics needs both to classify the visit
 * as social traffic rather than lumping it into "referral". utm_campaign was
 * the constant string 'product-share' on every product share, so it carried
 * no information that the /products/ path does not already give you — it was
 * 30 characters buying nothing.
 */
function addUtm(url: string, source: string): string {
  const u = new URL(url);
  u.searchParams.set('utm_source', source);
  u.searchParams.set('utm_medium', 'social');
  return u.toString();
}
export default function ShareButtons({
  url,
  title,
  description,
  imageUrl,
  locale
}: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const [hasNativeShare, setHasNativeShare] = useState(false);
  const isRTL = locale === 'ar';
  useEffect(() => {
    setHasNativeShare(!!navigator.share);
  }, []);
  const shareText = description ? `${title}\n${description}` : title;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${addUtm(url, 'whatsapp')}`)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(addUtm(url, 'facebook'))}`;
  const xUrl = `https://x.com/intent/tweet?url=${encodeURIComponent(addUtm(url, 'x'))}&text=${encodeURIComponent(title)}`;
  const pinterestUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(addUtm(url, 'pinterest'))}${imageUrl ? `&media=${encodeURIComponent(imageUrl)}` : ''}&description=${encodeURIComponent(title)}`;
  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(addUtm(url, 'copy-link'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = addUtm(url, 'copy-link');
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }
  async function handleNativeShare() {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title,
        text: description ?? title,
        url: addUtm(url, 'native-share')
      });
    } catch {
      // User cancelled — ignore
    }
  }
  const buttons = [{
    id: 'whatsapp',
    icon: <WhatsappLogo weight="fill" size={20} />,
    label: 'WhatsApp',
    href: whatsappUrl,
    className: 'hover:bg-[#25D366]/10 hover:text-[#25D366]'
  }, {
    id: 'facebook',
    icon: <FacebookLogo weight="fill" size={20} />,
    label: 'Facebook',
    href: facebookUrl,
    className: 'hover:bg-[#1877F2]/10 hover:text-[#1877F2]'
  }, {
    id: 'x',
    icon: <XLogo weight="bold" size={18} />,
    label: 'X',
    href: xUrl,
    className: 'hover:bg-foreground/10 hover:text-foreground'
  }, {
    id: 'pinterest',
    icon: <PinterestLogo weight="fill" size={20} />,
    label: 'Pinterest',
    href: pinterestUrl,
    className: 'hover:bg-[#E60023]/10 hover:text-[#E60023]'
  }];
  return <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm text-muted-foreground">
        {isRTL ? 'مشاركة:' : 'Share:'}
      </span>

      {buttons.map(btn => <a key={btn.id} href={btn.href} target="_blank" rel="noopener noreferrer" aria-label={`${isRTL ? 'مشاركة عبر' : 'Share on'} ${btn.label}`} className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground transition-colors ${btn.className}`}>
          {btn.icon}
        </a>)}

      <button type="button" onClick={handleCopyLink} aria-label={isRTL ? 'نسخ الرابط' : 'Copy link'} className="inline-flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground">
        {copied ? <Check size={20} weight="bold" className="text-green-600" /> : <LinkSimple size={20} />}
      </button>

      {hasNativeShare && <button type="button" onClick={handleNativeShare} aria-label={isRTL ? 'مشاركة' : 'Share'} className="inline-flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground">
          <ShareNetwork size={20} />
        </button>}
    </div>;
}